import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useSearchParams } from "react-router-dom";
import { saveStudentSubmittedAssignmentAPI } from "../../redux/features/courseSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const ShortQuestionMarking = ({ data }) => {
  const [questions, setQuestions] = useState([]);
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const validationSchema = Yup.object().shape({
    courseId: Yup.string().required("Course ID is required"),
    assignmentId: Yup.string().required("Assignment ID is required"),
    studentId: Yup.string().required("Student ID is required"),
    marksArray: Yup.array().of(
      Yup.object().shape({
        questionId: Yup.string().required("Question ID is required"),
        marksObtained: Yup.number()
          .typeError("Marks must be a number")
          .required("Marks are required")
          .min(0, "Marks cannot be negative"),
      })
    ),
  });

  useEffect(() => {
    setQuestions(data?.questions);
  }, [data]);

  let initialMarksArray =
    data?.questions?.length > 0
      ? data.questions.map((question) => ({
          questionId: question?.questionId || "",
          marksObtained: question?.marksObtained || Number(0),
        }))
      : [];

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      const res = await dispatch(
        saveStudentSubmittedAssignmentAPI({
          token: auth.token,
          id: searchParams.get("assignment_id"),
          course_id: searchParams.get("course_id"),
          student_id: searchParams.get("student_id"),
          data: values,
        })
      ).unwrap();
      if (res.success) {
        toast.success(res?.message, {
          position: "top-center",
        });
        navigate(-1);
      }
    } catch (error) {
      setStatus({ error: "Failed to submit marks. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="assignment-header fw-bold mb-10 mb-sm-20 mb-md-32 d-flex justify-content-between align-items-center ah-heading-04 flex-wrap gap-2">
        <span>{data?.assignmentTitle}</span>
        <div className="duration ah-text-md fw-bold theme-color">
          Duration:{" "}
          <span className="fw-semibold black-100 opacity-50">
            {data?.duration} min
          </span>
        </div>
      </div>
      <div className="card-default-style px-15 px-sm-25 px-md-35">
        <div className="assignment">
          <div
            className="assignment-inner overflow-hidden"
            style={{ maxHeight: "100%" }}
          >
            {initialMarksArray.length > 0 && (
              <Formik
                initialValues={{
                  courseId: searchParams.get("course_id") || "",
                  assignmentId: searchParams.get("assignment_id") || "",
                  studentId: searchParams.get("student_id") || "",
                  marksArray: initialMarksArray,
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  status,
                  isSubmitting,
                  setFieldTouched,
                  setFieldValue,
                }) => (
                  <Form>
                    {status?.success && (
                      <div className="alert alert-success">
                        {status.success}
                      </div>
                    )}
                    {status?.error && (
                      <div className="alert alert-danger">{status.error}</div>
                    )}
                    <ul className="p-0 list-unstyled mb-10 mb-sm-20 mb-md-32">
                      {questions?.map((question, index) => (
                        <li
                          key={question.questionId}
                          className="mb-18 d-flex align-items-start justify-content-between flex-column-reverse flex-sm-row gap-2 gap-lg-3 gap-xl-5"
                        >
                          <div className="question w-100">
                            <label
                              htmlFor={`marksArray.${index}.marksObtained`}
                              className="form-label fw-bold ah-text-md black-100 mb-14"
                            >
                              {index + 1}. {question?.questionText}
                            </label>
                            <div className="submittion-answer mb-14">
                              {question?.submittedAnswer ||
                                "No answer submitted"}
                            </div>
                            <label
                              htmlFor={`marksArray.${index}.marksObtained`}
                              className="form-label ah-text-base black-100 mb-14"
                            >
                              Add Marks
                            </label>
                            <Field
                              type="number"
                              min="0"
                              max={question?.marks}
                              step="0.1"
                              disabled={question?.submittedAnswer === (null || "") ? true : false}
                              name={`marksArray.${index}.marksObtained`}
                              id={`marksArray.${index}.marksObtained`}
                              className={`form-control ${
                                touched.marksArray?.[index]?.marksObtained &&
                                errors.marksArray?.[index]?.marksObtained
                                  ? "is-invalid"
                                  : ""
                              }`}
                              onChange={(e) => {
                                const value = e.target.value;
                                setFieldValue(
                                  `marksArray.${index}.marksObtained`,
                                  value ? parseFloat(value) : ""
                                );
                                setFieldValue(
                                  `marksArray.${index}.questionId`,
                                  question?.questionId
                                );
                              }}
                              onBlur={() => {
                                setFieldTouched(
                                  `marksArray.${index}.marksObtained`,
                                  true
                                );
                              }}
                              style={{ maxWidth: "144px", height: "47px" }}
                            />

                            {touched.marksArray?.[index]?.marksObtained &&
                              errors.marksArray?.[index]?.marksObtained && (
                                <div className="invalid-feedback d-block">
                                  {errors.marksArray[index].marksObtained}
                                </div>
                              )}
                            {values?.marksArray[index]?.marksObtained > 0 &&
                            values?.marksArray[index]?.marksObtained >
                              question?.marks ? (
                              <div className="invalid-feedback d-block">
                                Entered marks exceed the allowed limit of{" "}
                                {question?.marks}
                              </div>
                            ) : null}
                          </div>
                          <span className="points flex-shrink-0 h-auto px-29 py-8 ah-text-xs fw-semibold">
                            {question.marks || 1} Point
                          </span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-20">
                      <button
                        className="btn btn-primary no-shadow px-50 py-15"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Submitting..." : "Submit"}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ShortQuestionMarking;
